import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { CheckCircle2, ExternalLink, RotateCcw, Send, X } from 'lucide-react'
import { Button, Chip, KanbanBoard } from '@/components/ui'
import {
  useApproveQuote,
  useCustomerReject,
  usePoReceived,
  useConvertToSo,
  useRejectQuote,
  useTransitionQuote,
} from '../api/queries'
import type { QuoteStatus, QuoteSummary } from '../api/types'
import { QuoteStatusChip } from './QuoteStatusChip'

/**
 * Approval-pipeline KanbanBoard view (Sprint 12 Chunk 10).
 *
 * Stages: submitted → in_review → approved → sent → ordered.
 * `rejected` rows are filtered out into a side bucket (collapsed
 * by default) per spec; for v1 we just exclude them — visible via
 * the My Drafts tab in the list page.
 *
 * Drag-to-advance is restricted to legal state-machine edges:
 *   submitted → in_review (claim review)
 *   in_review → approved
 *   approved → sent  (re-render PDF + send email is invoked by the
 *                     /send action, not drag — drag only flips status
 *                     via /transition)
 *   approved | sent → ordered  (PO-gated, requires customer_po_number)
 *
 * Per-card actions (Approve/Reject/Record-PO/Convert-to-SO/Send)
 * surface the same workflow endpoints as the detail pane so the
 * approver can move quotes without leaving the board.
 */

const STAGES = [
  { id: 'draft', label: 'Draft', accent: 'neutral' as const },
  { id: 'in_review', label: 'Pricing In Review', accent: 'warning' as const },
  { id: 'revise_pricing', label: 'Revise Pricing', accent: 'warning' as const },
  { id: 'approved', label: 'Pricing Approved', accent: 'success' as const },
  { id: 'sent', label: 'Sent', accent: 'success' as const },
  { id: 'customer_approved', label: 'Customer Approved', accent: 'success' as const },
  { id: 'customer_rejected', label: 'Customer Rejected', accent: 'danger' as const },
]

const PIPELINE_STAGE_IDS: Set<QuoteStatus> = new Set([
  'draft', 'in_review', 'revise_pricing', 'approved',
  'sent', 'customer_approved', 'customer_rejected',
])

/** Encode the state-machine edges visible on the board. The order
 *  here mirrors `app/models/quotes.py::ALLOWED_TRANSITIONS` for the
 *  subset relevant to the pipeline view. */
const ALLOWED_DRAG_EDGES: ReadonlySet<string> = new Set([
  'draft->in_review',
  'in_review->approved',
  'in_review->revise_pricing',
  'revise_pricing->in_review',
  'revise_pricing->draft',
  'approved->sent',
  'approved->customer_approved',
  'sent->customer_approved',
  'sent->customer_rejected',
  'customer_rejected->draft',
])

// eslint-disable-next-line react-refresh/only-export-components -- helper colocated with the kanban it constrains; consumed by tests + the same component file.
export function isAllowedDragEdge(from: string, to: string): boolean {
  if (from === to) return false
  return ALLOWED_DRAG_EDGES.has(`${from}->${to}`)
}

interface CardItem {
  id: string
  stageId: string
  quote: QuoteSummary
}

export interface QuotePipelineBoardProps {
  quotes: QuoteSummary[]
  onSelect?: (q: QuoteSummary) => void
  selectedId?: string
}

export function QuotePipelineBoard({
  quotes, onSelect, selectedId,
}: QuotePipelineBoardProps) {
  const transition = useTransitionQuote()
  const approve = useApproveQuote()
  const reject = useRejectQuote()
  const customerReject = useCustomerReject()
  const recordPo = usePoReceived()
  const convert = useConvertToSo()
  const navigate = useNavigate()

  const items = useMemo<CardItem[]>(
    () =>
      quotes
        .filter((q) => PIPELINE_STAGE_IDS.has(q.status))
        .map((q) => ({
          id: String(q.id),
          stageId: q.status,
          quote: q,
        })),
    [quotes],
  )

  const handleAdvance = (
    item: CardItem, from: string, to: string,
  ) => {
    // Use the convenience endpoints when they map cleanly (cleaner audit
    // history). Fall through to /transition otherwise.
    const id = item.quote.id
    if (from === 'in_review' && to === 'approved') {
      approve.mutate(id)
      return
    }
    if (from === 'in_review' && to === 'revise_pricing') {
      const reason = window.prompt('Reason for revision?') ?? ''
      if (!reason) return
      reject.mutate({ id, body: { reason } })
      return
    }
    if (from === 'sent' && to === 'customer_rejected') {
      const reason = window.prompt('Why did the customer reject?') ?? ''
      if (!reason) return
      customerReject.mutate({ id, body: { reason } })
      return
    }
    if ((from === 'approved' || from === 'sent') && to === 'customer_approved') {
      // Pre-check PO: if no PO recorded, prompt + record then convert.
      if (!item.quote.customer_po_number) {
        const num = window.prompt('Customer PO number?')
        if (!num) return
        recordPo.mutate(
          { id, body: { customer_po_number: num } },
          {
            onSuccess: () => convert.mutate(id),
          },
        )
        return
      }
      convert.mutate(id)
      return
    }
    transition.mutate({ id, body: { to: to as QuoteStatus } })
  }

  return (
    <KanbanBoard<CardItem>
      stages={STAGES}
      items={items}
      onAdvance={handleAdvance}
      allowedTransitions={isAllowedDragEdge}
      onSelect={(item) => onSelect?.(item.quote)}
      selectedId={selectedId}
      renderCard={(item) => <QuotePipelineCard
        quote={item.quote}
        onApprove={() => approve.mutate(item.quote.id)}
        onReject={() => {
          const reason = window.prompt('Reason for revision?') ?? ''
          if (reason) reject.mutate({ id: item.quote.id, body: { reason } })
        }}
        onRevise={() =>
          transition.mutate(
            { id: item.quote.id, body: { to: 'draft' } },
            {
              onSuccess: () => {
                navigate({
                  to: '/quotes/$quoteId',
                  params: { quoteId: String(item.quote.id) },
                })
              },
            },
          )
        }
      />}
    />
  )
}

interface CardProps {
  quote: QuoteSummary
  onApprove: () => void
  onReject: () => void
  onRevise: () => void
}

function QuotePipelineCard({ quote, onApprove, onReject, onRevise }: CardProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="font-semibold">{quote.quote_number}</span>
        <QuoteStatusChip status={quote.status} />
      </div>
      <div className="text-xs text-secondary">
        {quote.customer_name || `customer #${quote.customer_id}`}
        {' · '}
        {quote.line_count} line{quote.line_count === 1 ? '' : 's'}
      </div>
      {quote.customer_po_number ? (
        <Chip size="xs" variant="info">PO {quote.customer_po_number}</Chip>
      ) : null}
      {quote.odoo_so_id ? (
        <Chip size="xs" variant="success">SO #{quote.odoo_so_id}</Chip>
      ) : null}
      {quote.status === 'in_review' ? (
        <div
          className="flex items-center gap-1 mt-1"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Button
            size="sm"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation()
              onApprove()
            }}
          >
            <CheckCircle2 size={12} />
            Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onReject()
            }}
          >
            <X size={12} />
            Request revisions
          </Button>
        </div>
      ) : null}
      {(quote.status === 'revise_pricing' || quote.status === 'customer_rejected') ? (
        <div
          className="flex items-center gap-1 mt-1"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {quote.rejection_reason ? (
            <span className="text-xs text-danger" title={quote.rejection_reason}>
              {quote.rejection_reason.slice(0, 36)}
              {quote.rejection_reason.length > 36 ? '…' : ''}
            </span>
          ) : null}
          <Button
            size="sm"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation()
              onRevise()
            }}
          >
            <RotateCcw size={12} />
            Revise
          </Button>
        </div>
      ) : null}
      {quote.status === 'approved' && quote.customer_po_number === null ? (
        <div className="text-xs text-tertiary mt-1">
          <Send size={11} className="inline mr-1" />
          Drag to Sent or record PO to convert
        </div>
      ) : null}
      {quote.status === 'customer_approved' && quote.odoo_so_id ? (
        <div className="text-xs text-secondary mt-1">
          <ExternalLink size={11} className="inline mr-1" />
          Linked to Odoo SO #{quote.odoo_so_id}
        </div>
      ) : null}
    </div>
  )
}
